/**
 * SpeecargoTheme - Main Configuration
 * Theme configuration builder functions
 */

import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';
import { DARK_COMPONENTS, LIGHT_COMPONENTS } from './components';
import {
	PANAM_COMPONENTS,
	PANAM_LAYOUT_COLORS,
	PANAM_THEME_TOKENS,
} from './themes/panam';
import {
	SPEEDCARGO_COMPONENTS,
	SPEEDCARGO_LAYOUT_COLORS,
	SPEEDCARGO_THEME_TOKENS,
} from './themes/speedcargo';
import {
	TWA_COMPONENTS,
	TWA_LAYOUT_COLORS,
	TWA_THEME_TOKENS,
} from './themes/twa';
import type { ThemeMode } from './tokens';
import { DARK_THEME_TOKENS, LAYOUT_COLORS, LIGHT_THEME_TOKENS } from './tokens';

export interface ThemeSettings {
	mode: ThemeMode;
	colorPrimary: string;
}

/**
 * Get theme settings based on current mode
 */
export function getThemeSettings(mode: ThemeMode): ThemeSettings {
	const primaryColors: Record<ThemeMode, string> = {
		light: '#111111',
		dark: '#1890ff',
		speedcargo: '#1890ff',
		twa: '#C8102E',
		panam: '#003E7E',
	};

	return {
		mode,
		colorPrimary: primaryColors[mode],
	};
}

/**
 * Get Ant Design theme config for the given mode
 */
export function getThemeConfig(mode: ThemeMode): ThemeConfig {
	switch (mode) {
		case 'speedcargo':
			return {
				token: SPEEDCARGO_THEME_TOKENS,
				components: SPEEDCARGO_COMPONENTS,
				algorithm: antdTheme.darkAlgorithm,
			};
		case 'dark':
			return {
				token: DARK_THEME_TOKENS,
				components: DARK_COMPONENTS,
				algorithm: antdTheme.darkAlgorithm,
			};
		case 'twa':
			return {
				token: TWA_THEME_TOKENS,
				components: TWA_COMPONENTS,
				algorithm: antdTheme.defaultAlgorithm,
			};
		case 'panam':
			return {
				token: PANAM_THEME_TOKENS,
				components: PANAM_COMPONENTS,
				algorithm: antdTheme.darkAlgorithm,
			};
		case 'light':
		default:
			return {
				token: LIGHT_THEME_TOKENS,
				components: LIGHT_COMPONENTS,
				algorithm: antdTheme.defaultAlgorithm,
			};
	}
}

/**
 * Get algorithm configuration for ConfigProvider
 */
export function getAlgorithm(mode: ThemeMode) {
	// Light theme and TWA use default algorithm, others use dark
	return mode === 'light' || mode === 'twa'
		? [antdTheme.defaultAlgorithm]
		: [antdTheme.darkAlgorithm];
}

/**
 * Get layout colors for the given theme mode
 */
export function getLayoutColors(mode?: ThemeMode) {
	if (!mode) {
		return SPEEDCARGO_LAYOUT_COLORS; // Default to Speedcargo theme
	}

	switch (mode) {
		case 'speedcargo':
			return SPEEDCARGO_LAYOUT_COLORS;
		case 'twa':
			return TWA_LAYOUT_COLORS;
		case 'panam':
			return PANAM_LAYOUT_COLORS;
		case 'dark':
		case 'light':
		default:
			return LAYOUT_COLORS;
	}
}

/**
 * Get CSS class name for the current theme mode
 */
export function getThemeClassName(mode: ThemeMode): string {
	const themeClassNames: Record<ThemeMode, string> = {
		light: 'light-theme',
		dark: 'dark-theme',
		speedcargo: 'speedcargo-theme',
		twa: 'twa-theme',
		panam: 'panam-theme',
	};

	return themeClassNames[mode];
}
